import { Fragment, useState } from 'react';
import { Badge, Card, ErrorMessage, Field, Loading, Modal, Toggle, useAsync, useToast } from '../../components/ui';
import { RowActions } from '../../components/RowActions';
import { IconPlus } from '../../components/icons';
import { DsfaBadge } from '../../components/DsfaBadge';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useTableView, type ViewColumn } from '../../lib/tableView';

/** Spalten für Sortierung und Gruppierung; die Darstellung steckt weiterhin in der Tabelle. */
const CHANNEL_COLUMNS: ViewColumn<any>[] = [
  { key: 'name', label: 'Kanal', value: (row) => row.name },
  { key: 'key', label: 'Schlüssel', value: (row) => row.key },
  {
    key: 'allow_anonymous',
    label: 'Ohne Login',
    value: (row) => (row.allow_anonymous ? 1 : 0),
    label_of: (row) => (row.allow_anonymous ? 'Ohne Login' : 'Anmeldung nötig'),
    groupable: true,
  },
  {
    key: 'requires_reporter_contact',
    label: 'Kontaktdaten Melder',
    value: (row) => (row.requires_reporter_contact ? 1 : 0),
    label_of: (row) => (row.requires_reporter_contact ? 'Kontaktdaten Pflicht' : 'Ohne Kontaktdaten'),
    groupable: true,
  },
  {
    key: 'auto_triage',
    label: 'Automatische Vorkategorisierung',
    value: (row) => (row.auto_triage ? 1 : 0),
    label_of: (row) => (row.auto_triage ? 'Vorkategorisierung aktiv' : 'Ohne Vorkategorisierung'),
    groupable: true,
  },
  { key: 'task_count', label: 'Aufträge', value: (row) => row.task_count ?? 0, numeric: true },
  {
    key: 'is_active',
    label: 'Status',
    value: (row) => (row.is_active ? 1 : 0),
    label_of: (row) => (row.is_active ? 'Aktiv' : 'Stillgelegt'),
    groupable: true,
  },
];

/** Kanalschlüssel, für die eine Bot-Anbindung (Zugangsdaten, Verbindungstest) angeboten wird. */
const BOT_CHANNEL_KINDS: Record<string, 'telegram' | 'whatsapp' | 'webchat'> = {
  telegram: 'telegram',
  whatsapp: 'whatsapp',
  webchat: 'webchat',
};

/**
 * Meldekanäle (Kap. 9) – aus dem Servicekatalog in die Systemeinstellungen
 * verschoben. Ein Kanal wird als Metadatum an jeder darüber gemeldeten
 * Aufgabe gespeichert, deshalb bleibt der Schlüssel nach dem Anlegen fest.
 */
export function ChannelsSettings() {
  const { can } = useAuth();
  const { notify } = useToast();
  const channels = useAsync(() => api.get('/api/catalogs/channels'), []);
  const [modal, setModal] = useState<string | null>(null);

  const mayEdit = can('admin.catalogs.manage');
  const findChannel = (id: string) => (channels.data ?? []).find((entry: any) => String(entry.id) === id);

  const view = useTableView<any>(channels.data ?? [], CHANNEL_COLUMNS);
  const spaltenzahl = 8 + (mayEdit ? 1 : 0);

  return (
    <div className="stack">
      <Card
        title="Meldekanäle"
        subtitle="Jeder Kanal wird als Metadatum am Auftrag gespeichert"
        actions={
          mayEdit ? (
            <button className="btn primary sm" onClick={() => setModal('new')}>
              <IconPlus width={14} height={14} /> Meldekanal anlegen
            </button>
          ) : undefined
        }
        noBody
      >
        <div className="card-body tight">
          <label className="toolbar-field">
            <span>Gruppieren</span>
            <select value={view.groupBy} onChange={(event) => view.setGroupBy(event.target.value)}>
              <option value="">ohne</option>
              {CHANNEL_COLUMNS.filter((column) => column.groupable).map((column) => (
                <option key={column.key} value={column.key}>
                  {column.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                {CHANNEL_COLUMNS.slice(0, 5).map((column) => (
                  <th
                    key={column.key}
                    className="clickable"
                    onClick={() => view.toggleSort(column.key)}
                    title={`Nach „${column.label}“ sortieren`}
                  >
                    {column.label} <span className="subtle">{view.sortIndicator(column.key)}</span>
                  </th>
                ))}
                <th>Formular</th>
                <th className="num clickable" onClick={() => view.toggleSort('task_count')} title="Nach Aufträgen sortieren">
                  Aufträge <span className="subtle">{view.sortIndicator('task_count')}</span>
                </th>
                <th className="clickable" onClick={() => view.toggleSort('is_active')} title="Nach Status sortieren">
                  Status <span className="subtle">{view.sortIndicator('is_active')}</span>
                </th>
                {mayEdit && <th className="actions">Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {view.groups.map((gruppe) => (
                <Fragment key={gruppe.label ?? 'alle'}>
                  {gruppe.label !== null && (
                    <tr className="group-row">
                      <td colSpan={spaltenzahl}>
                        <strong>{gruppe.label}</strong> <span className="subtle small">({gruppe.rows.length})</span>
                      </td>
                    </tr>
                  )}
                  {gruppe.rows.map((channel: any) => (
                <tr key={channel.id} style={channel.is_active ? undefined : { opacity: 0.55 }}>
                  <td>
                    <strong>{channel.name}</strong>
                  </td>
                  <td className="mono small">{channel.key}</td>
                  <td>{channel.allow_anonymous ? <Badge variant="warning">ja</Badge> : <span className="small subtle">nein</span>}</td>
                  <td>
                    {channel.requires_reporter_contact ? (
                      <>
                        <Badge variant="info">Pflicht</Badge> <DsfaBadge id="channels.contact" />
                      </>
                    ) : (
                      <span className="small subtle">–</span>
                    )}
                  </td>
                  <td>{channel.auto_triage ? <Badge variant="info">aktiv</Badge> : <span className="small subtle">–</span>}</td>
                  <td className="small subtle">
                    {channel.form_schema?.fields ? `${channel.form_schema.fields.length} Felder` : 'Standard'}
                  </td>
                  <td className="num">{channel.task_count}</td>
                  <td>
                    <Badge variant={channel.is_active ? 'success' : ''}>
                      {channel.is_active ? 'Aktiv' : 'Stillgelegt'}
                    </Badge>
                  </td>
                  {mayEdit && (
                    <td className="actions">
                      {channel.is_active ? (
                        <RowActions
                          label={channel.name}
                          onEdit={() => setModal(`edit-${channel.id}`)}
                          deletePath={`/api/catalogs/channels/${channel.id}${channel.task_count > 0 ? '' : '?permanent=true'}`}
                          confirmText={
                            channel.task_count > 0
                              ? `Über „${channel.name}“ wurden ${channel.task_count} Aufträge gemeldet. Der Kanal wird stillgelegt und bleibt in der Auswertung sichtbar. Fortfahren?`
                              : `Meldekanal „${channel.name}“ löschen?`
                          }
                          onDeleted={() => channels.reload()}
                        />
                      ) : (
                        <button
                          className="btn sm"
                          onClick={async () => {
                            await api.post(`/api/catalogs/channels/${channel.id}/reactivate`);
                            notify('Meldekanal wieder aktiv.', 'success');
                            channels.reload();
                          }}
                        >
                          Reaktivieren
                        </button>
                      )}
                    </td>
                  )}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {(modal === 'new' || modal?.startsWith('edit-')) && (
        <ChannelModal
          channel={modal.startsWith('edit-') ? findChannel(modal.replace('edit-', '')) : undefined}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            channels.reload();
            notify('Meldekanal gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

/**
 * Meldekanal.
 *
 * Der Schlüssel wandert in jede darüber gemeldete Aufgabe und lässt sich
 * deshalb nachträglich nicht mehr ändern – sonst zeigten alte Meldungen auf
 * einen Kanal, den es unter diesem Namen nie gab.
 */
function ChannelModal({ channel, onClose, onSaved }: { channel?: any; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    key: channel?.key ?? '',
    name: channel?.name ?? '',
    description: channel?.description ?? '',
    allowAnonymous: !!channel?.allow_anonymous,
    autoTriage: !!channel?.auto_triage,
    requiresReporterContact: !!channel?.requires_reporter_contact,
    isActive: channel ? !!channel.is_active : true,
  });
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title={channel ? `Meldekanal bearbeiten – ${channel.name}` : 'Meldekanal anlegen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.key || !form.name}
            onClick={async () => {
              try {
                if (channel) await api.patch(`/api/catalogs/channels/${channel.id}`, form);
                else await api.post('/api/catalogs/channels', form);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Bezeichnung" required>
            <input
              value={form.name}
              autoFocus
              onChange={(event) =>
                setForm({
                  ...form,
                  name: event.target.value,
                  key: channel ? form.key : form.key || event.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                })
              }
            />
          </Field>
          <Field label="Schlüssel" required hint={channel ? 'Nach dem Anlegen unveränderlich.' : undefined}>
            <input
              value={form.key}
              disabled={!!channel}
              onChange={(event) => setForm({ ...form, key: event.target.value })}
            />
          </Field>
        </div>
        <Field label="Beschreibung">
          <input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Field>
        <Toggle
          checked={form.allowAnonymous}
          onChange={(value) => setForm({ ...form, allowAnonymous: value })}
          label="Meldung ohne Anmeldung zulassen (z. B. QR-Code am Objekt)"
        />
        <Toggle
          checked={form.requiresReporterContact}
          onChange={(value) => setForm({ ...form, requiresReporterContact: value })}
          label="Kontaktdaten des Melders abfragen (Name, Vorname, Telefon oder E-Mail)"
        />
        <Toggle
          checked={form.autoTriage}
          onChange={(value) => setForm({ ...form, autoTriage: value })}
          label="Automatisch vorkategorisieren"
        />
        <Toggle
          checked={form.isActive}
          onChange={(value) => setForm({ ...form, isActive: value })}
          label="Aktiv"
        />

        {channel && BOT_CHANNEL_KINDS[channel.key] && (
          <BotIntegrationSection channelId={channel.id} kind={BOT_CHANNEL_KINDS[channel.key]} />
        )}
      </div>
    </Modal>
  );
}

/**
 * Zugangsdaten einer Bot-Anbindung – nach dem `maptiler_key`-Muster: Der
 * Token wird nie ausgeliefert, nur ob einer hinterlegt ist. Eigener
 * Speichern-Knopf, damit ein leeres Feld beim gemeinsamen Kanal-Speichern
 * nicht versehentlich den Token löscht.
 */
function BotIntegrationSection({ channelId, kind }: { channelId: number; kind: 'telegram' | 'whatsapp' | 'webchat' }) {
  const { notify } = useToast();
  const data = useAsync(() => api.get(`/api/catalogs/channels/${channelId}/integration`), [channelId]);
  const [token, setToken] = useState('');
  const [origin, setOrigin] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    botUsername?: string;
    phoneNumber?: string;
    verifiedName?: string;
    error?: string;
  } | null>(null);

  if (data.loading) return <Loading />;

  const integration = data.data?.integration;
  const hasToken = !!integration?.has_bot_token;
  const configured =
    kind === 'webchat'
      ? !!integration?.external_ref
      : kind === 'whatsapp'
        ? hasToken && !!integration?.external_ref
        : hasToken;

  const save = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const body =
        kind === 'webchat'
          ? { kind, allowedOrigin: origin }
          : kind === 'whatsapp'
            ? { kind, botToken: token, phoneNumberId, appSecret }
            : { kind, botToken: token };
      const result = await api.put(`/api/catalogs/channels/${channelId}/integration`, body);
      setToken('');
      setOrigin('');
      setPhoneNumberId('');
      setAppSecret('');
      data.reload();
      notify(
        result.lastError ? `Gespeichert, aber der Anbieter meldet: ${result.lastError}` : 'Bot-Anbindung gespeichert.',
        result.lastError ? 'error' : 'success',
      );
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Bot-Anbindung entfernen? Der Webhook wird beim Anbieter abgemeldet.')) return;
    setBusy(true);
    try {
      await api.delete(`/api/catalogs/channels/${channelId}/integration`);
      data.reload();
      notify('Bot-Anbindung entfernt.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Entfernen fehlgeschlagen.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const result = await api.post(`/api/catalogs/channels/${channelId}/integration/test`);
      setTestResult(result);
    } catch (error) {
      setTestResult({ ok: false, error: error instanceof Error ? error.message : 'Prüfung fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="record-form" style={{ borderColor: 'var(--border)', boxShadow: 'none' }}>
      <div className="row" style={{ gap: 8, marginBottom: 10 }}>
        <strong style={{ fontSize: 13.5 }}>Bot-Anbindung ({kind === 'telegram' ? 'Telegram' : kind === 'whatsapp' ? 'WhatsApp' : 'Web-Chatbot'})</strong>
        <span className="spacer" />
        <DsfaBadge id={`bot.${kind}`} />
        <Badge variant={configured ? 'success' : ''}>{configured ? 'Hinterlegt' : 'Nicht hinterlegt'}</Badge>
      </div>

      {kind === 'telegram' && (
        <>
          <Field
            label={hasToken ? 'Bot-Token ersetzen' : 'Bot-Token hinterlegen'}
            hint="Von @BotFather bei der Anlage des Bots vergeben. Der Webhook wird beim Speichern automatisch eingerichtet."
          >
            <input
              type="password"
              autoComplete="off"
              placeholder={hasToken ? '•••••••• (unverändert lassen)' : '123456:ABC-DEF...'}
              value={token}
              onChange={(event) => setToken(event.target.value)}
            />
          </Field>

          {integration?.external_ref && (
            <div className="small subtle" style={{ marginBottom: 8 }}>
              Verbunden mit @{integration.external_ref}
            </div>
          )}
          {integration?.last_error && (
            <div className="alert error" style={{ fontSize: 12.5, marginBottom: 8 }}>
              {integration.last_error}
            </div>
          )}
          {testResult && (
            <div className={`alert ${testResult.ok ? 'success' : 'error'}`} style={{ fontSize: 12.5, marginBottom: 8 }}>
              {testResult.ok ? `Verbunden mit @${testResult.botUsername}` : testResult.error}
            </div>
          )}

          <div className="row end" style={{ gap: 8 }}>
            {hasToken && (
              <>
                <button className="btn sm ghost" disabled={busy} onClick={remove}>
                  Entfernen
                </button>
                <button className="btn sm" disabled={busy} onClick={test}>
                  Verbindung prüfen
                </button>
              </>
            )}
            <button className="btn sm primary" disabled={busy || token.trim().length < 8} onClick={save}>
              Token speichern
            </button>
          </div>
        </>
      )}

      {kind === 'webchat' && (
        <>
          <Field
            label={configured ? 'Freigegebene Domäne ersetzen' : 'Domäne freigeben'}
            hint="Vollständige Adresse der Website, auf der das Widget eingebunden wird, z. B. https://www.kunde.de"
          >
            <input
              type="text"
              autoComplete="off"
              placeholder={integration?.external_ref || 'https://www.kunde.de'}
              value={origin}
              onChange={(event) => setOrigin(event.target.value)}
            />
          </Field>

          {integration?.embedUrl && (
            <div style={{ marginBottom: 8 }}>
              <div className="small subtle" style={{ marginBottom: 4 }}>
                Einbettung – dieses Element auf der freigegebenen Seite platzieren:
              </div>
              <code className="mono small" style={{ display: 'block', wordBreak: 'break-all', background: 'var(--surface-2)', padding: 8, borderRadius: 6 }}>
                {`<script src="${integration.embedUrl}"></script>`}
              </code>
            </div>
          )}

          <div className="row end" style={{ gap: 8 }}>
            {configured && (
              <button className="btn sm ghost" disabled={busy} onClick={remove}>
                Entfernen
              </button>
            )}
            <button className="btn sm primary" disabled={busy || origin.trim().length < 8} onClick={save}>
              Domäne speichern
            </button>
          </div>
        </>
      )}

      {kind === 'whatsapp' && (
        <>
          <div className="form-grid">
            <Field
              label={hasToken ? 'Zugriffstoken ersetzen' : 'Zugriffstoken hinterlegen'}
              hint="Dauerhafter Zugriffstoken aus dem Meta-App-Dashboard (WhatsApp → API-Setup)."
            >
              <input
                type="password"
                autoComplete="off"
                placeholder={hasToken ? '•••••••• (unverändert lassen)' : 'EAAG...'}
                value={token}
                onChange={(event) => setToken(event.target.value)}
              />
            </Field>
            <Field label="Telefonnummer-ID" hint="Aus dem Meta-App-Dashboard (WhatsApp → API-Setup → Phone number ID).">
              <input
                type="text"
                autoComplete="off"
                placeholder={integration?.external_ref || '109876543210'}
                value={phoneNumberId}
                onChange={(event) => setPhoneNumberId(event.target.value)}
              />
            </Field>
          </div>
          <Field
            label={integration?.has_webhook_secret ? 'App-Secret ersetzen' : 'App-Secret hinterlegen'}
            hint="Aus den App-Grundeinstellungen bei Meta – prüft, dass eingehende Nachrichten wirklich von Meta stammen."
          >
            <input
              type="password"
              autoComplete="off"
              placeholder={integration?.has_webhook_secret ? '•••••••• (unverändert lassen)' : 'App-Secret'}
              value={appSecret}
              onChange={(event) => setAppSecret(event.target.value)}
            />
          </Field>

          {integration?.webhookUrl && (
            <div style={{ marginBottom: 8 }}>
              <div className="small subtle" style={{ marginBottom: 4 }}>
                Im Meta-App-Dashboard unter WhatsApp → Konfiguration einzutragen:
              </div>
              <div className="stack" style={{ gap: 4 }}>
                <div>
                  <span className="small subtle">Rückruf-URL: </span>
                  <code className="mono small" style={{ wordBreak: 'break-all' }}>{integration.webhookUrl}</code>
                </div>
                <div>
                  <span className="small subtle">Verify-Token: </span>
                  <code className="mono small" style={{ wordBreak: 'break-all' }}>{integration.verifyToken}</code>
                </div>
              </div>
            </div>
          )}

          {integration?.last_error && (
            <div className="alert error" style={{ fontSize: 12.5, marginBottom: 8 }}>
              {integration.last_error}
            </div>
          )}
          {testResult && (
            <div className={`alert ${testResult.ok ? 'success' : 'error'}`} style={{ fontSize: 12.5, marginBottom: 8 }}>
              {testResult.ok ? `Verbunden: ${testResult.verifiedName} (${testResult.phoneNumber})` : testResult.error}
            </div>
          )}

          <div className="row end" style={{ gap: 8 }}>
            {configured && (
              <>
                <button className="btn sm ghost" disabled={busy} onClick={remove}>
                  Entfernen
                </button>
                <button className="btn sm" disabled={busy} onClick={test}>
                  Verbindung prüfen
                </button>
              </>
            )}
            <button
              className="btn sm primary"
              disabled={busy || (!hasToken && token.trim().length < 8)}
              onClick={save}
            >
              Speichern
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Telegram-Rückkanal (Kap. 5, Etappe 4).
 *
 * Mandantenweit statt an einen Meldekanal gebunden – deshalb keine eigene
 * Katalogzeile, sondern ein eigenes Register auf der Meldekanäle-Seite
 * (`MeldekanaeleSettings.tsx`). Derselbe Aufbau wie `BotIntegrationSection`
 * (Token-Feld, Verbindung prüfen, Entfernen), aber gegen eigene,
 * kanalunabhängige Endpunkte.
 */
export function ReplyChannelCard() {
  const { notify } = useToast();
  const data = useAsync(() => api.get('/api/catalogs/reply-channels/telegram'), []);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; botUsername?: string; error?: string } | null>(null);

  if (data.loading) return <Loading />;

  const replyChannel = data.data?.replyChannel;
  const hasToken = !!replyChannel?.has_bot_token;

  const save = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const result = await api.put('/api/catalogs/reply-channels/telegram', { botToken: token });
      setToken('');
      data.reload();
      notify(
        result.lastError ? `Gespeichert, aber der Anbieter meldet: ${result.lastError}` : 'Rückkanal gespeichert.',
        result.lastError ? 'error' : 'success',
      );
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Telegram-Rückkanal entfernen? Der Webhook wird beim Anbieter abgemeldet.')) return;
    setBusy(true);
    try {
      await api.delete('/api/catalogs/reply-channels/telegram');
      data.reload();
      notify('Rückkanal entfernt.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Entfernen fehlgeschlagen.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const result = await api.post('/api/catalogs/reply-channels/telegram/test');
      setTestResult(result);
    } catch (error) {
      setTestResult({ ok: false, error: error instanceof Error ? error.message : 'Prüfung fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="row" style={{ gap: 8 }}>
        <Badge variant={hasToken ? 'success' : ''}>{hasToken ? 'Hinterlegt' : 'Nicht hinterlegt'}</Badge>
      </div>

      <Field
        label={hasToken ? 'Bot-Token ersetzen' : 'Bot-Token hinterlegen'}
        hint="Ein eigener, bei @BotFather angelegter Bot – nicht derselbe wie beim Meldekanal Telegram. Der Webhook wird beim Speichern automatisch eingerichtet."
      >
        <input
          type="password"
          autoComplete="off"
          placeholder={hasToken ? '•••••••• (unverändert lassen)' : '123456:ABC-DEF...'}
          value={token}
          onChange={(event) => setToken(event.target.value)}
        />
      </Field>

      <div className="row">
        <DsfaBadge id="bot.reply" />
      </div>

      {replyChannel?.external_ref && (
        <div className="small subtle">Verbunden mit @{replyChannel.external_ref}</div>
      )}
      {replyChannel?.last_error && <div className="alert error" style={{ fontSize: 12.5 }}>{replyChannel.last_error}</div>}
      {testResult && (
        <div className={`alert ${testResult.ok ? 'success' : 'error'}`} style={{ fontSize: 12.5 }}>
          {testResult.ok ? `Verbunden mit @${testResult.botUsername}` : testResult.error}
        </div>
      )}

      <div className="row end" style={{ gap: 8 }}>
        {hasToken && (
          <>
            <button className="btn sm ghost" disabled={busy} onClick={remove}>
              Entfernen
            </button>
            <button className="btn sm" disabled={busy} onClick={test}>
              Verbindung prüfen
            </button>
          </>
        )}
        <button className="btn sm primary" disabled={busy || token.trim().length < 8} onClick={save}>
          Token speichern
        </button>
      </div>
    </div>
  );
}
